import { useMobxStores } from '@stores/index';
import SearchStore from '@/stores/SearchStore';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { IFacility, IPriceDescription } from '@/types';
import Head from 'next/head';
import { formatCurrency } from '@/helpers/format';

interface IPTitles {
  title: string;
}

interface IFacilityInfo {
  facility_branded: number;
  facility_lot?: string;
  facility_url_code: string;
  airport_initials: string;
  airport_name: string;
  facility_product_type: string;
  facility_description: string;
  facilityParkingType?: number;
  facility_selling_price?: number;
  currency_facility_selling_price?: number;
  currency_name?: string;
}

function formatFacilityPrice(bookingPriceDetail: IPriceDescription): string {
  const daysParked = bookingPriceDetail.daysParked ? bookingPriceDetail.daysParked : 1
  if (bookingPriceDetail?.currency_name == 'CAD') {
    let cadPrice = bookingPriceDetail.currency_base_price / daysParked
    if (bookingPriceDetail.facility_rate_first_tier > 0) {
      cadPrice = bookingPriceDetail.facility_rate_first_tier;
    }
    return formatCurrency({
      value: cadPrice,
      locale: 'en-CA',
      currency: 'CAD',
    });
  }
  let val = bookingPriceDetail.basePrice / daysParked;
  if (bookingPriceDetail.facility_rate_first_tier > 0) {
    val = bookingPriceDetail.facility_rate_first_tier;
  }
  return formatCurrency({
    value: val,
    locale: 'en-US',
    currency: 'USD',
  });
}

function extractSeoValues(info: IFacilityInfo, bookingPriceDetail: IPriceDescription) {
  if (info.facilityParkingType == 2) {
    return {
      ogTitle: null,
      description: `${info.facility_description}.`,
    };
  }

  const formattedPrice = formatFacilityPrice(bookingPriceDetail);

  return {
    ogTitle: info.facilityParkingType == 1
      ? info.facility_branded == 1
        ? `${info.facility_lot} - Reserve Your Cruise Parking Here`
        : `Special Offer - Reserve ${info?.airport_name} Parking From ${formattedPrice}`
      : info.facility_branded == 1
        ? `${info.facility_lot} - Reserve Your ${info.airport_initials} Parking Spot Here`
        : `Special Offer - Reserve Your ${info.airport_initials} Parking Spot Here From ${formattedPrice}`,
    description: info.facilityParkingType == 1
      ? info.facility_branded == 1
        ? `Skip pricey cruiseport lots and book a better parking rate with ${info.facility_lot}. Easy, affordable cruiseport parking—reserve today.`
        : `Skip pricey ${info?.airport_name} parking lots and book parking from just ${formattedPrice}. Easy, affordable cruiseport parking—reserve today.`
      : info.facility_branded == 1
        ? `Skip pricey airport lots and book a better parking rate near (${info.airport_initials}) at ${info.facility_lot}. Easy, affordable airport parking—reserve today.`
        : `Skip pricey airport lots and book parking near ${info.airport_initials} at ${formattedPrice}. Easy, affordable airport parking—reserve today.`,
  };
}

export function extractFacilityName(info: IFacilityInfo): string {
  const reg2 = new RegExp('\\d+', 'g');
  if (info.facility_branded == 1) {
    return info.facility_lot;
  } else {
    const arr = info.facility_url_code.match(reg2);
    let number = '';
    if (arr) {
      number = ' ' + arr[0];
    }
    if (info.facility_product_type) {
      return info.facility_product_type + ' ' + number;
    } else {
      return info.facility_lot;
    }
  }
}
export function extractFacilityFullName(info: IFacilityInfo): string {
  const facilityName = extractFacilityName(info);

  if (!info.airport_name || !info.airport_initials) {
    return facilityName;
  }

  return (
    facilityName +
    ' - ' +
    info.airport_name +
    ' (' +
    info.airport_initials +
    ')'
  );
}
function TitleParkingAt({ info, bookingPriceDetail }: { info: IFacilityInfo, bookingPriceDetail: IPriceDescription }) {
  let pageTitle = extractFacilityFullName(info);
  if (!info.facility_branded && !info.facility_product_type) {
    pageTitle = `Cheapest Airport Parking at ${info.facility_lot} | On Air Parking
    Discount Rates `;
  }
  const { ogTitle, description } = extractSeoValues(info, bookingPriceDetail);
  return (
    <>
      <Head>
        <title>{ogTitle ?? pageTitle}</title>
        <meta property="og:title" content={ogTitle ?? pageTitle} key="og:title" />

        <meta
          name="description"
          content={description}
          key="description"
        />
        <meta
          property="og:description"
          content={description}
          key="og:description"
        />
      </Head>
    </>
  );
}

export default observer(TitleParkingAt);
